import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Container, Row, Col } from 'reactstrap';
import classes from './DepotSettings.module.scss';
import get from '../../../services/axios/get';
import patch from '../../../services/axios/patch';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { customAlert } from '../../../store/actions/alert';
import Loader from '../../UI/Loader/Loader';
import Input from '../../UI/Input/Input';
import Button from '../../UI/Button/Button';
import DatePicker from '../../UI/DatePicker/DatePicker';
import Modal from '../../../containers/Modal/Modal';
import PluginDepot from '../../../models/pluginDepot';

interface DateSpecificDepotSettings {
  openTime: string;
  closeTime: string;
}

interface DepotWithDateSettings extends PluginDepot {
  dateSpecificSettings?: { [date: string]: DateSpecificDepotSettings };
}

const DepotSettings: React.FC = () => {
  const dispatch = useAppDispatch();
  const user = useAppSelector(state => state.auth.user);
  const isMounted = useRef(true);
  const [depot, setDepot] = useState<DepotWithDateSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSavingSlots, setIsSavingSlots] = useState(false);
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [copyFromDate, setCopyFromDate] = useState<string>('');
  const [copyToDate, setCopyToDate] = useState<string>('');
  const [daysToApply, setDaysToApply] = useState<number>(1);
  const [availableSlots, setAvailableSlots] = useState<number>(0);
  const [currentSettings, setCurrentSettings] = useState<DateSpecificDepotSettings>({
    openTime: '08:00',
    closeTime: '17:00'
  });

  const timeOptions = Array.from({ length: 48 }, (_, i) => {
    const hour = Math.floor(i / 2).toString().padStart(2, '0');
    const minute = i % 2 === 0 ? '00' : '30';
    const time = `${hour}:${minute}`;
    return { value: time, displayValue: time };
  });

  const formatDate = (date: Date): string => {
    const localDate = new Date(date.getTime() - (date.getTimezoneOffset() * 60000));
    return localDate.toISOString().split('T')[0];
  };

  const addDaysToDate = (dateString: string, days: number): string => {
    const date = new Date(dateString);
    date.setDate(date.getDate() + days);
    return formatDate(date);
  };

  const getConfiguredDates = (): string[] => {
    const today = formatDate(new Date());
    return Object.keys(depot?.dateSpecificSettings || {})
      .filter(date => date >= today)
      .sort();
  };

  const getDepotDetails = useCallback(() => {
    get(
      `/depot`,
      {},
      (res) => {
        console.log('Depot details response:', res);
        if (isMounted.current) {
          const depotData = res.data[0];
          setDepot(depotData);
          setAvailableSlots(depotData.availableSlots || 0);
          const mostRecentDate = depotData?.dateSpecificSettings ?
            Object.keys(depotData.dateSpecificSettings).sort().pop() || formatDate(new Date()) :
            formatDate(new Date());
          setSelectedDate(mostRecentDate);
          loadSettingsForDate(mostRecentDate, depotData);
          setIsLoading(false);
        }
      },
      (err) => {
        console.error('Error fetching depot details:', err);
        if (isMounted.current) {
          dispatch(customAlert(true, 'Failed to load depot details'));
          setIsLoading(false);
        }
      }
    );
  }, [dispatch]);

  useEffect(() => {
    getDepotDetails();
    return () => {
      isMounted.current = false;
    };
  }, [getDepotDetails]);

  const loadSettingsForDate = (date: string, depotData?: DepotWithDateSettings) => {
    const targetDepot = depotData || depot;
    const settings = targetDepot?.dateSpecificSettings?.[date];
    if (settings) {
      setCurrentSettings(settings);
    } else {
      const defaultSettings: DateSpecificDepotSettings = {
        openTime: '08:00',
        closeTime: '17:00'
      };
      setCurrentSettings(defaultSettings);
    }
  };

  const handleDateSelection = (date: string) => {
    setSelectedDate(date);
    loadSettingsForDate(date);
  };

  const handleNewDateSelection = (date: Date | null, isValid: boolean) => {
    if (isValid && date && depot?.id) {
      const formattedDate = formatDate(date);

      const defaultSettings: DateSpecificDepotSettings = {
        openTime: '08:00',
        closeTime: '17:00'
      };

      const updatedSettings = {
        ...depot.dateSpecificSettings,
        [formattedDate]: defaultSettings
      };

      setIsSavingSettings(true);
      patch(
        `/depot/${depot.id}`,
        { dateSpecificSettings: updatedSettings },
        () => {
          if (isMounted.current) {
            setShowDatePicker(false);
            setSelectedDate(formattedDate);
            dispatch(customAlert(true, `Settings created for ${new Date(formattedDate).toLocaleDateString()}`));
            setIsSavingSettings(false);
            getDepotDetails();
          }
        },
        (err) => {
          if (isMounted.current) {
            dispatch(customAlert(true, err || 'Failed to create settings for new date'));
            setIsSavingSettings(false);
          }
        }
      );
    }
  };

  const updateTime = (field: 'openTime' | 'closeTime', value: string) => {
    setCurrentSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const copySettingsFromDate = () => {
    if (!copyFromDate || !copyToDate || !depot?.dateSpecificSettings?.[copyFromDate]) {
      dispatch(customAlert(true, 'Please select valid dates to copy from and to'));
      return;
    }

    const settingsToCopy = depot.dateSpecificSettings[copyFromDate];
    setCurrentSettings(settingsToCopy);
    setSelectedDate(copyToDate);
    setShowCopyModal(false);
    setCopyFromDate('');
    setCopyToDate('');
    dispatch(customAlert(true, 'Settings copied successfully'));
  };

  const updateAvailableSlots = (value: string) => {
    const numValue = parseInt(value) || 0;
    const cappedValue = Math.min(numValue, 999);
    setAvailableSlots(cappedValue);
  };

  const saveAvailableSlots = () => {
    if (!depot?.id) return;

    setIsSavingSlots(true);
    patch(
      `/depot/${depot.id}`,
      { availableSlots },
      () => {
        if (isMounted.current) {
          dispatch(customAlert(true, 'Available slots updated successfully'));
          setIsSavingSlots(false);
          getDepotDetails();
        }
      },
      (err) => {
        if (isMounted.current) {
          dispatch(customAlert(true, err || 'Failed to update available slots'));
          setIsSavingSlots(false);
        }
      }
    );
  };

  const saveSettings = () => {
    if (!depot?.id || !selectedDate) return;

    const updatedSettings = { ...depot.dateSpecificSettings };

    for (let i = 0; i < daysToApply; i++) {
      const targetDate = addDaysToDate(selectedDate, i);
      updatedSettings[targetDate] = { ...currentSettings };
    }

    setIsSavingSettings(true);
    patch(
      `/depot/${depot.id}`,
      { dateSpecificSettings: updatedSettings },
      () => {
        if (isMounted.current) {
          const message = daysToApply === 1
            ? 'Depot settings updated successfully'
            : `Depot settings updated for ${daysToApply} consecutive days`;
          dispatch(customAlert(true, message));
          setIsSavingSettings(false);
          getDepotDetails();
        }
      },
      (err) => {
        if (isMounted.current) {
          dispatch(customAlert(true, err || 'Failed to update depot settings'));
          setIsSavingSettings(false);
        }
      }
    );
  };

  if (isLoading) {
    return (
      <Container>
        <div className={classes.loaderContainer}>
          <Loader size="large" />
        </div>
      </Container>
    );
  }

  if (!depot) {
    return (
      <Container>
        <div className={classes.errorContainer}>
          <h4>No depot found</h4>
          {!user?.pluginDepotId ? (
            <p>You don't have a plugin depot assigned to your account. Please contact an administrator.</p>
          ) : (
            <p>Unable to load depot information. Please try again or contact support.</p>
          )}
        </div>
      </Container>
    );
  }

  const configuredDates = getConfiguredDates();

  return (
    <Container>
      <Row>
        <Col>
          <div className={classes.header}>
            <h2>Depot Settings</h2>
            <h4>{depot.alias}</h4>
          </div>
        </Col>
      </Row>

      <Row>
        <Col>
          <div className={classes.section}>
            <h5>Available Slots</h5>
            <Input
              elementType="input"
              elementConfig={{
                type: 'number',
                placeholder: 'Number of available slots',
                min: '0',
                max: '999'
              }}
              value={availableSlots.toString()}
              change={(e) => updateAvailableSlots(e.target.value)}
              id="available-slots"
              label={`Available Slots${availableSlots >= 999 ? ' (999+)' : ''}`}
              inputStyle="main"
            />
            <div className='mt-2'>
              <Button
                type="button"
                buttonStyle="main"
                text={isSavingSlots ? 'Saving...' : 'Update Slots'}
                click={saveAvailableSlots}
                disabled={isSavingSlots}
              />
            </div>
          </div>
        </Col>
      </Row>

      <Row>
        <Col>
          <div className={classes.section}>
            <h5>Date Selection</h5>
            <div className={classes.dateControls}>
              <div className={classes.dateSelection}>
                <select
                  className={classes.dateDropdown}
                  value={selectedDate}
                  onChange={(e) => handleDateSelection(e.target.value)}
                >
                  {configuredDates.length > 0 ? (
                    configuredDates.sort((a, b) => new Date(b).getTime() - new Date(a).getTime()).map(date => (
                      <option key={date} value={date}>
                        {new Date(date).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </option>
                    ))
                  ) : (
                    <option value={selectedDate}>
                      {new Date(selectedDate).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </option>
                  )}
                </select>
                <Button
                  type="button"
                  buttonStyle="main"
                  text="Add New Date"
                  click={() => setShowDatePicker(true)}
                />
              </div>
            </div>
          </div>
        </Col>
      </Row>

      <Row>
        <Col md={6}>
          <div className={classes.section}>
            <h5>Apply to Multiple Days</h5>
            <Input
              elementType="input"
              elementConfig={{
                type: 'number',
                placeholder: 'Number of consecutive days',
                min: '1',
                max: '30'
              }}
              value={daysToApply.toString()}
              change={(e) => setDaysToApply(Math.max(1, Math.min(30, parseInt(e.target.value) || 1)))}
              id="days-to-apply"
              label="Days to Apply Settings"
              inputStyle="main"
            />
            <small>Settings will be applied to {daysToApply} consecutive day{daysToApply !== 1 ? 's' : ''} starting from the selected date</small>
          </div>
        </Col>

        <Col>
          <div className={classes.section}>
            <h5>Operating Hours for {new Date(selectedDate).toLocaleDateString()}</h5>
            <div className={classes.timeInputs}>
              <div className={classes.timeInput}>
                <Input
                  elementType="select"
                  elementConfig={{
                    type: 'select',
                    placeholder: 'Open Time',
                    options: timeOptions
                  }}
                  value={currentSettings.openTime ?? ''}
                  change={(e) => updateTime('openTime', e.target.value)}
                  id="open-time"
                  label="Open Time"
                  inputStyle="main"
                />
              </div>
              <div className={classes.timeInput}>
                <Input
                  elementType="select"
                  elementConfig={{
                    type: 'select',
                    placeholder: 'Close Time',
                    options: timeOptions
                  }}
                  value={currentSettings.closeTime}
                  change={(e) => updateTime('closeTime', e.target.value)}
                  id="close-time"
                  label="Close Time"
                  inputStyle="main"
                />
              </div>
            </div>
          </div>
        </Col>
      </Row>

      <Row>
        <Col>
          <div className={classes.actions}>
            <Button
              type="button"
              buttonStyle="main"
              text={isSavingSettings ? 'Saving...' : 'Save Time Settings'}
              click={saveSettings}
              disabled={isSavingSettings}
            />
          </div>
        </Col>
      </Row>

      {showDatePicker && (
        <Modal close={() => setShowDatePicker(false)} style={{ width: '400px' }}>
          <div>
            <h5>Select Date for New Settings</h5>
            <DatePicker
              defaultDate={new Date()}
              callback={handleNewDateSelection}
              invalidMessage="Please select a valid future date"
            />
          </div>
        </Modal>
      )}

      {showCopyModal && (
        <Modal close={() => setShowCopyModal(false)} style={{ width: '500px' }}>
          <div>
            <h5>Copy Settings Between Dates</h5>
            <div className={classes.copyForm}>
              <div className={classes.formGroup}>
                <label>Copy From:</label>
                <select
                  className={classes.dateDropdown}
                  value={copyFromDate}
                  onChange={(e) => setCopyFromDate(e.target.value)}
                >
                  <option value="">Select source date</option>
                  {configuredDates.map(date => (
                    <option key={date} value={date}>
                      {new Date(date).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </option>
                  ))}
                </select>
              </div>
              <div className={classes.formGroup}>
                <label>Copy To:</label>
                <input
                  type="date"
                  className={classes.dateInput}
                  value={copyToDate}
                  onChange={(e) => setCopyToDate(e.target.value)}
                  min={formatDate(new Date())}
                />
              </div>
              <div className={classes.modalActions}>
                <Button
                  type="button"
                  buttonStyle="main"
                  text="Copy"
                  click={copySettingsFromDate}
                  disabled={!copyFromDate || !copyToDate}
                />
                <Button
                  type="button"
                  buttonStyle="main-red"
                  text="Cancel"
                  click={() => {
                    setShowCopyModal(false);
                    setCopyFromDate('');
                    setCopyToDate('');
                  }}
                />
              </div>
            </div>
          </div>
        </Modal>
      )}
    </Container>
  );
};

export default DepotSettings;
